import {
  ROUTER_PATHS,
  getUrlParamsFetch,
  handleAxiosError,
  toApiError,
  urlBackend,
} from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { UseFetchEnabledParams } from '@/shared/interfaces';
import {
  IDataProducto,
  IProductoPagingRes,
  ProductoForm,
  ProductosParam,
} from '@/shared/interfaces/erp/administration/producto.interface';
import { useUiStore } from '@/store/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const { get, post, put } = erpApi();

export const useCreateProducto = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const navigate = useNavigate();

  return useMutation({
    mutationFn: postProducto,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['producto'] });

      if (data.code == 200) navigate(ROUTER_PATHS.inventario.productosNav);
      setAlert(true, data.message, data.status);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const postProducto = async (data: ProductoForm) => {
  const resp = await post<IProductoPagingRes>(
    `${urlBackend}api/v1/producto/`,
    data,
    true
  );
  return resp;
};

export const useFetchProductos = ({
  params, enabled = true
}: UseFetchEnabledParams<ProductosParam>) => {
  return useQuery({
    queryKey: ['producto', ...Object.values(params || {})],
    queryFn: async () => {
      return await getProductos(params);
    },
    retry: false,
    enabled,
  });
};

export const getProductos = async (params?: ProductosParam) => {
  const queryParams = getUrlParamsFetch(params || {});

  const resp = await get<IProductoPagingRes>(
    `${urlBackend}api/v1/producto-simple/?${queryParams}`,
    true
  );

  if (resp.code == 502) {
    console.log('ERROR DEL SERVIDOR');
  }
  return resp;
};

export const useFetchProductoById = (id?: string, enabled = false) => {
  return useQuery({
    queryKey: ['producto', id],
    queryFn: async () => {
      return await getProductoById(id);
    },
    enabled: enabled,
  });
};

export const getProductoById = async (id?: string) => {
  const resp = await get<IDataProducto>(
    `${urlBackend}api/v1/producto/${id}`,
    true
  );

  return resp.data;
};

export const useFetchPutProducto = ({
  returnUrl
}:{returnUrl?: string}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ProductoForm> }) => {
      return putProducto(id, data);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['producto'] });

      if (data.code == 200) navigate(returnUrl || ROUTER_PATHS.inventario.productosNav);

      setAlert(true, data.message, data.status);
    },
  });
};

export const putProducto = async (id: string, data: Partial<ProductoForm>) => {
  const resp = await put<IDataProducto>(
    `${urlBackend}api/v1/producto/${id}/`,
    data,
    true
  );

  return resp;
};

export const useFetchProductosByCode = (
  codes: string[],
  enabled = false,
  params?: ProductosParam
) => {
  return useQuery({
    queryKey: ['producto', codes],
    queryFn: async () => {
      return await getProductosByCode(codes, params);
    },
    enabled,
  });
};
export const useFetchProductosByCodeEnabled = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<ProductosParam> = {}) => {
  return useQuery({
    queryKey: ['producto', ...Object.values(params || {})],
    queryFn: async () => {
      return await getProductosByCode(params?.codigos || ([] as any), params);
    },
    enabled,
  });
};

export const useFetchProductosByIds = (ids: number[], enabled = true) => {
  return useQuery({
    queryKey: ['producto', ids],
    queryFn: async () => {
      return await getProductosByIds(ids);
    },
    enabled,
  });
};

export const getProductosByCode = async (
  codes: string[],
  params?: ProductosParam
) => {
  const urlCodes = codes.map(code => `${code}`).join(',');
  const queryParams = getUrlParamsFetch(params || {});

  const resp = await get<IProductoPagingRes>(
    `${urlBackend}api/v1/producto-simple/?codigos=${urlCodes}&${queryParams}`,
    true
  );

  return resp;
};

export const getProductosByIds = async (ids: number[]) => {
  const urlIds = ids.map(id => `ids=${id}`).join('&');

  const resp = await get<IProductoPagingRes>(
    `${urlBackend}api/v1/productos-por-ids/?${urlIds}`,
    true
  );

  return resp;
};


///* Importar excel

export const useCreateInventariableUploadExcel = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createInventariableUploadExcel,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['producto'] });

      if (data.code == 200)
        setAlert(true, data.message, data.status);

    },
    onError: error => {      

      const res = toApiError(error)
      
      toast.error(res.message);
      
      
      //handleAxiosError(error);
    },
  });
};

//FormData
export const createInventariableUploadExcel = async (data: { url: string }) => {
  const res = await post<IDataProducto>(`${urlBackend}api/v1/subir-excel-crear-productos/`, data, true);
  return res;
};


///* producto autocomplete

export const fetchAutocompleteProducto = async (
  searchValue: string,
  page: number
): Promise<{ options: IDataProducto[]; hasMore: boolean }> => {
  const query = getUrlParamsFetch({
    estado: true,
    codigo: searchValue,
    page,
  });

  const response = await get<IProductoPagingRes>(`${urlBackend}api/v1/producto/?${query}`, true);
  return {
    options: response.data.data,
    hasMore: true,
  };
};


/// producto simple

export const useFetchProductoSimple = ({
  params, enabled = true
}: UseFetchEnabledParams<ProductosParam>) => {
  return useQuery({
    queryKey: ['producto-simple', ...Object.values(params || {})],
    queryFn: async () => {
      return await getProductoSimple(params);
    },
    retry: false,
    enabled,
  });
};

export const getProductoSimple = async (params?: ProductosParam) => {
  const queryParams = getUrlParamsFetch(params || {});

  const resp = await get<IProductoPagingRes>(
    `${urlBackend}api/v1/producto-simple/?${queryParams}`,
    true
  );

  if (resp.code == 502) {
    console.log('ERROR DEL SERVIDOR');
  }
  return resp;
};


///* obtener producto simple

export const useFetchProductoSimpleById = (id?: string, enabled = false) => {
  return useQuery({
    queryKey: ['producto-simple', id],
    queryFn: async () => {
      return await getProductoSimpleById(id);
    },
    enabled: enabled,
  });
};

export const getProductoSimpleById = async (id?: string) => {
  const resp = await get<IDataProducto>(
    `${urlBackend}api/v1/producto-simple/${id}`,
    true
  );

  return resp.data;
};